/**
 * Generic reactor design classes — v1 catalog (site-level selection).
 * Vendor-specific entries (NuScale, Xe-100, …) land in v2.
 */
import type { ProjectIntent } from '../types/siteRag';

export type ReactorDesignClassId =
  | 'lwr-smr'
  | 'htgr'
  | 'molten-salt'
  | 'microreactor'
  | 'large-lwr'
  | 'undecided';

export type ReactorFuelClass = 'LEU' | 'HALEU' | 'mixed' | 'unknown';

export interface ReactorDesignClassEntry {
  id: ReactorDesignClassId;
  label: string;
  fuelClass: ReactorFuelClass;
  /** Class midpoint for BoM module-count heuristics until v2 vendor catalog. */
  typicalModuleMWe: number;
  landAcresPer100MW: number;
  typicalCodYears: { low: number; high: number };
  licensingPathwaySummary: string;
  kbTags: readonly string[];
  damSlotHints: {
    fuelClass: ReactorFuelClass;
    epzModel: 'source-term-reduced' | 'traditional' | 'micro-transportable';
    haleuApplies: boolean;
  };
}

export const REACTOR_DESIGN_CATALOG: Record<ReactorDesignClassId, ReactorDesignClassEntry> = {
  'lwr-smr': {
    id: 'lwr-smr',
    label: 'LWR-based SMR',
    fuelClass: 'LEU',
    typicalModuleMWe: 77,
    landAcresPer100MW: 1.5,
    typicalCodYears: { low: 5, high: 7 },
    licensingPathwaySummary:
      '10 CFR Part 52 ESP/COL pathway; passively safe LWR SMRs may qualify for source-term-based reduced EPZ at site boundary.',
    kbTags: ['lwr', 'leu', 'smr', 'licensing'],
    damSlotHints: {
      fuelClass: 'LEU',
      epzModel: 'source-term-reduced',
      haleuApplies: false,
    },
  },
  htgr: {
    id: 'htgr',
    label: 'HTGR / TRISO',
    fuelClass: 'HALEU',
    typicalModuleMWe: 80,
    landAcresPer100MW: 2,
    typicalCodYears: { low: 6, high: 8 },
    licensingPathwaySummary:
      'Part 52 COL with HALEU/TRISO fuel cycle; HALEU supply-chain readiness is a gating constraint; source-term EPZ reduction plausible for low source-term designs.',
    kbTags: ['htgr', 'haleu', 'triso', 'smr', 'licensing'],
    damSlotHints: {
      fuelClass: 'HALEU',
      epzModel: 'source-term-reduced',
      haleuApplies: true,
    },
  },
  'molten-salt': {
    id: 'molten-salt',
    label: 'Molten-salt SMR',
    fuelClass: 'LEU',
    typicalModuleMWe: 195,
    landAcresPer100MW: 1.8,
    typicalCodYears: { low: 6, high: 9 },
    licensingPathwaySummary:
      'Novel fuel cycle; pre-licensing / first-of-a-kind regulatory path; typically standard LEU (<5%) — HALEU not required for IMSR-class designs.',
    kbTags: ['molten-salt', 'imsr-class', 'smr', 'licensing'],
    damSlotHints: {
      fuelClass: 'LEU',
      epzModel: 'source-term-reduced',
      haleuApplies: false,
    },
  },
  microreactor: {
    id: 'microreactor',
    label: 'Microreactor',
    fuelClass: 'mixed',
    typicalModuleMWe: 50,
    landAcresPer100MW: 1,
    typicalCodYears: { low: 4, high: 7 },
    licensingPathwaySummary:
      'NRC microreactor / transportable pathway; fuel class varies by vendor (LEU or HALEU); reduced footprint but novel logistics and security.',
    kbTags: ['microreactor', 'smr', 'licensing'],
    damSlotHints: {
      fuelClass: 'mixed',
      epzModel: 'micro-transportable',
      haleuApplies: true,
    },
  },
  'large-lwr': {
    id: 'large-lwr',
    label: 'Large LWR',
    fuelClass: 'LEU',
    typicalModuleMWe: 1000,
    landAcresPer100MW: 5,
    typicalCodYears: { low: 8, high: 12 },
    licensingPathwaySummary:
      'Traditional 10/50-mile EPZ; full Part 50/52 licensing; longest timeline and highest land/exclusion-area requirements.',
    kbTags: ['large-nuclear', 'epz-traditional', 'licensing'],
    damSlotHints: {
      fuelClass: 'LEU',
      epzModel: 'traditional',
      haleuApplies: false,
    },
  },
  undecided: {
    id: 'undecided',
    label: 'Not selected',
    fuelClass: 'unknown',
    typicalModuleMWe: 77,
    landAcresPer100MW: 1.5,
    typicalCodYears: { low: 5, high: 8 },
    licensingPathwaySummary: 'Reactor design class not yet selected — treat nuclear licensing and fuel-cycle findings as conditional.',
    kbTags: ['smr', 'nuclear'],
    damSlotHints: {
      fuelClass: 'unknown',
      epzModel: 'source-term-reduced',
      haleuApplies: false,
    },
  },
};

export const REACTOR_DESIGN_CLASS_LABEL: Record<ReactorDesignClassId, string> = Object.fromEntries(
  Object.values(REACTOR_DESIGN_CATALOG).map(e => [e.id, e.label]),
) as Record<ReactorDesignClassId, string>;

export const REACTOR_DESIGN_CLASS_IDS = Object.keys(REACTOR_DESIGN_CATALOG) as ReactorDesignClassId[];

export function getReactorDesignClass(id: ReactorDesignClassId | undefined): ReactorDesignClassEntry {
  if (!id || !(id in REACTOR_DESIGN_CATALOG)) {
    return REACTOR_DESIGN_CATALOG.undecided;
  }
  return REACTOR_DESIGN_CATALOG[id];
}

export function resolveReactorDesignFromIntent(
  intent: ProjectIntent | null | undefined,
): ReactorDesignClassEntry {
  if (!intent) return REACTOR_DESIGN_CATALOG.undecided;
  if (intent.nuclearInterest === 'no' || intent.nuclearInterest === 'undecided') {
    return REACTOR_DESIGN_CATALOG.undecided;
  }
  return getReactorDesignClass(intent.reactorDesignClass);
}

export function intentShowsReactorDesignPicker(intent: ProjectIntent | null | undefined): boolean {
  return intent?.nuclearInterest === 'primary' || intent?.nuclearInterest === 'considering';
}

export function reactorDesignKbTags(intent: ProjectIntent | null | undefined): string[] {
  const entry = resolveReactorDesignFromIntent(intent);
  return [...entry.kbTags];
}

/** v2 deferred capabilities — surfaced in UI footnotes and admin stubs. */
export const REACTOR_DESIGN_V2_DEFERRED = [
  {
    id: 'vendor-catalog',
    label: 'Vendor-specific designs (NuScale, Xe-100, BWRX-300) with NRC docket IDs and supplier linkage',
  },
  {
    id: 'adams-docket-fetch',
    label: 'ADAMS auto-fetch keyed to vendor docket',
  },
  {
    id: 'reactor-enrichment-job',
    label: 'Cloud Run enrichment job for reactor vendor research',
  },
] as const;

/** One-line scope note for Project Intent / overview surfaces. */
export const REACTOR_DESIGN_V1_SCOPE_NOTE =
  'Generic design class only. Vendor-specific designs and NRC docket linkage are available when a specific vendor is selected.';
